import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { 
  ArrowLeft, Zap, Trophy, Clock, ChevronRight,
  CheckCircle, XCircle, Play, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

const QUIZ_LEVEL_SECTION_REQS = { 1: 0, 2: 5, 3: 10, 4: 15, 5: 20, 6: 21 };

function isLevelUnlocked(lvl, tier, sectionsCompleted) {
  if (tier === "educator") return { locked: false, reason: null };
  if (tier === "free") {
    if (lvl === 1) return { locked: false, reason: null };
    return { locked: true, reason: "Upgrade to access higher levels" };
  }
  const req = QUIZ_LEVEL_SECTION_REQS[lvl] || 0;
  const sc = new Set(sectionsCompleted || []);
  if (req === 0) return { locked: false, reason: null };
  if (req === 21) {
    if (sc.size >= 21) return { locked: false, reason: null };
    return { locked: true, reason: `Complete all 21 sections to unlock` };
  }
  if (sc.has(req)) return { locked: false, reason: null };
  return { locked: true, reason: `Complete Section ${req} to unlock` };
}

export default function QuizArena() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState("menu");
  const [level, setLevel] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [answerState, setAnswerState] = useState("idle");
  const [selectedOption, setSelectedOption] = useState(null);
  const [mascotMood, setMascotMood] = useState("neutral");

  const sectionsCompleted = user?.sections_completed || [];
  const tier = user?.purchase_tier || "free";

  const startGame = async (selectedLevel) => {
    setLoading(true);
    try {
      const res = await api.get(`/games/quiz-arena/questions/${selectedLevel}`);
      setQuestions(res.data.questions);
      setLevel(selectedLevel);
      setCurrentQuestion(0);
      setAnswers({});
      setScore(0);
      setStartTime(Date.now());
      setGameState("playing");
      setAnswerState("idle");
      setSelectedOption(null);
      setMascotMood("neutral");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer) => {
    if (answerState !== "idle") return;
    
    const isCorrect = answer === questions[currentQuestion].correct;
    setSelectedOption(answer);
    setAnswers(prev => ({ ...prev, [currentQuestion]: answer }));
    
    setAnswerState("evaluating");
    
    setTimeout(() => {
      setAnswerState("revealed");
      
      if (isCorrect) {
        setScore(prev => prev + 1);
        setMascotMood("happy");
      } else {
        setMascotMood("sad");
      }
      
      setTimeout(() => {
        setAnswerState("idle");
        setSelectedOption(null);
        setMascotMood("neutral");
        
        if (currentQuestion < questions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
        } else {
          submitGame(isCorrect ? score + 1 : score);
        }
      }, 1200);
    }, 500);
  };

  const submitGame = async (finalScore) => {
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      const res = await api.post("/games/quiz-arena/submit", {
        game_type: "quiz_arena",
        level,
        score: finalScore,
        correct_answers: finalScore,
        time_spent: timeSpent
      });
      
      setResults({
        ...res.data,
        correct: finalScore,
        total: questions.length,
        time: timeSpent
      });
      setGameState("results");
      
      if (res.data.xp_earned > 0) {
        toast.success(`+${res.data.xp_earned} BTC XP earned!`);
      }
    } catch (error) {
      toast.error("Failed to submit score");
      setGameState("menu");
    }
  };

  // Determine next level status for results screen
  const getNextLevelInfo = () => {
    if (level >= 6) return null;
    const nextLvl = level + 1;
    const lockInfo = isLevelUnlocked(nextLvl, tier, sectionsCompleted);
    return { level: nextLvl, ...lockInfo };
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => gameState === "playing" ? setGameState("menu") : navigate("/dashboard")}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="quiz-arena-back-btn"
            >
              <ArrowLeft size={20} />
              <span>{gameState === "playing" ? "Quit" : "Dashboard"}</span>
            </button>
            <div className="flex items-center gap-3">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-8 h-8 rounded-lg" />
              <span className="font-heading text-lg font-bold text-white">
                QUIZ <span className="text-[#F7931A]">ARENA</span>
              </span>
            </div>
            <div />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* Menu State */}
          {gameState === "menu" && (
            <motion.div
              key="menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain Mascot" className="w-32 h-32 mx-auto rounded-2xl mb-8 btc-glow" />
              <h1 className="font-heading text-4xl font-bold text-white mb-4">
                QUIZ <span className="text-[#F7931A]">ARENA</span>
              </h1>
              <p className="text-zinc-400 mb-8">
                Test your Bitcoin knowledge and earn BTC XP! Higher levels = harder questions = more rewards.
              </p>

              {tier === "free" && (
                <div className="bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-xl p-4 flex items-center gap-3 max-w-2xl mx-auto mb-6" data-testid="quiz-upgrade-banner">
                  <Lock className="text-[#F7931A] flex-shrink-0" size={18} />
                  <div className="flex-1 text-left">
                    <p className="text-white text-sm font-medium">Level 1 available free</p>
                    <p className="text-zinc-500 text-xs">Upgrade to unlock all 6 quiz levels and earn more BTC XP</p>
                  </div>
                  <Button size="sm" className="btn-primary text-xs h-7" onClick={() => navigate("/pricing")} data-testid="quiz-upgrade-btn">
                    Upgrade
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
                {[1, 2, 3, 4, 5, 6].map((lvl) => {
                  const lockInfo = isLevelUnlocked(lvl, tier, sectionsCompleted);
                  const isLocked = lockInfo.locked;
                  return (
                    <button
                      key={lvl}
                      onClick={() => !isLocked && startGame(lvl)}
                      disabled={isLocked || loading}
                      className={`p-6 rounded-xl border transition-all ${
                        isLocked 
                          ? "bg-zinc-900 border-zinc-800 opacity-50 cursor-not-allowed"
                          : "bg-[#0A0A0A] border-white/10 hover:border-[#F7931A]/50 cursor-pointer hover:scale-[1.02]"
                      }`}
                      data-testid={`level-${lvl}-btn`}
                    >
                      <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                        isLocked ? "bg-zinc-800" : "bg-[#F7931A]/20"
                      }`}>
                        {isLocked ? <Lock className="text-zinc-600" size={20} /> :
                        <span className={`font-heading text-2xl font-bold text-[#F7931A]`}>
                          {lvl}
                        </span>}
                      </div>
                      <p className={`font-medium ${isLocked ? "text-zinc-600" : "text-white"}`}>Level {lvl}</p>
                      <p className="text-zinc-500 text-sm">
                        {isLocked ? (lockInfo.reason || "Locked") : `${5 + lvl} questions`}
                      </p>
                    </button>
                  );
                })}
              </div>

              {loading && (
                <div className="mt-8 flex items-center justify-center gap-2 text-[#F7931A]">
                  <div className="w-5 h-5 border-2 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
                  Loading questions...
                </div>
              )}
            </motion.div>
          )}

          {/* Playing State */}
          {gameState === "playing" && questions.length > 0 && (
            <motion.div
              key="playing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-500">Level {level}</span>
                  <span className="text-zinc-500">{currentQuestion + 1}/{questions.length}</span>
                </div>
                <Progress value={(currentQuestion / questions.length) * 100} className="h-2" />
              </div>

              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="flex items-center gap-2 bg-[#F7931A]/10 px-4 py-2 rounded-lg">
                  <Trophy className="text-[#F7931A]" size={18} />
                  <span className="font-mono text-white">{score} correct</span>
                </div>
                <motion.div
                  animate={
                    mascotMood === "happy" ? { y: [0, -6, 0], transition: { duration: 0.4, ease: "easeOut" } } :
                    mascotMood === "sad" ? { y: [0, 3, 0], transition: { duration: 0.6, ease: "easeInOut" } } :
                    {}
                  }
                >
                  <img 
                    src={MASCOT_IMAGE} 
                    alt="Mascot" 
                    className={`w-10 h-10 rounded-lg transition-all duration-300 ${
                      mascotMood === "happy" ? "btc-glow" : 
                      mascotMood === "sad" ? "opacity-50 grayscale-[40%]" : ""
                    }`}
                  />
                </motion.div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-8 mb-6">
                    <p className="text-white text-xl font-medium text-center" data-testid="quiz-arena-question">
                      {questions[currentQuestion].question}
                    </p>
                  </div>

                  <div className="grid gap-3">
                    {questions[currentQuestion].options.map((option, index) => {
                      const isSelected = selectedOption === option;
                      const isRevealed = answerState === "revealed";
                      const isCorrect = isRevealed && option === questions[currentQuestion].correct;
                      const isWrong = isRevealed && isSelected && option !== questions[currentQuestion].correct;
                      const isEvaluating = answerState === "evaluating" && isSelected;
                      
                      return (
                        <button
                          key={index}
                          onClick={() => handleAnswer(option)}
                          disabled={answerState !== "idle"}
                          className={`p-4 rounded-lg border text-left transition-all duration-500 ${
                            isCorrect
                              ? "border-green-500 bg-green-500/20 text-green-400"
                              : isWrong
                                ? "border-red-500 bg-red-500/20 text-red-400"
                                : isEvaluating
                                  ? "border-[#F7931A] bg-[#F7931A]/10 text-white"
                                  : isSelected && !isRevealed
                                    ? "border-[#F7931A] bg-[#F7931A]/10 text-white"
                                    : "border-white/10 bg-[#161616] text-zinc-300 hover:border-[#F7931A]/50"
                          }`}
                          data-testid={`answer-${index}`}
                        >
                          <span className="flex items-center gap-3">
                            {isCorrect && <CheckCircle size={20} className="flex-shrink-0" />}
                            {isWrong && <XCircle size={20} className="flex-shrink-0" />}
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

          {/* Results State */}
          {gameState === "results" && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <motion.img
                src={MASCOT_IMAGE}
                alt="Bitcoin Brain"
                className={`w-32 h-32 mx-auto rounded-2xl mb-8 ${results.correct >= results.total * 0.7 ? "btc-glow" : "opacity-70"}`}
                animate={results.correct >= results.total * 0.7 
                  ? { y: [0, -8, 0], transition: { repeat: Infinity, duration: 2, ease: "easeInOut" } }
                  : {}
                }
              />
              
              <h1 className="font-heading text-4xl font-bold text-white mb-4">
                {results.correct >= results.total * 0.7 ? (
                  <>GREAT <span className="text-[#F7931A]">JOB!</span></>
                ) : (
                  <>KEEP <span className="text-[#F7931A]">LEARNING!</span></>
                )}
              </h1>
              
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                  <Trophy className="text-[#F7931A] mx-auto mb-2" size={24} />
                  <p className="font-mono text-2xl text-white">{results.correct}/{results.total}</p>
                  <p className="text-zinc-500 text-sm">Correct</p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                  <Clock className="text-[#F7931A] mx-auto mb-2" size={24} />
                  <p className="font-mono text-2xl text-white">{Math.floor(results.time / 60)}:{(results.time % 60).toString().padStart(2, '0')}</p>
                  <p className="text-zinc-500 text-sm">Time</p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                  <Zap className="text-[#F7931A] mx-auto mb-2" size={24} />
                  <p className="font-mono text-2xl text-white">+{results.xp_earned}</p>
                  <p className="text-zinc-500 text-sm">BTC XP</p>
                </div>
              </div>

              {results.level_unlocked && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-8 p-4 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-lg"
                >
                  <p className="text-[#F7931A] font-medium">Level {level + 1} Unlocked!</p>
                </motion.div>
              )}

              <div className="flex gap-4 justify-center">
                <Button onClick={() => setGameState("menu")} className="btn-secondary" data-testid="quiz-back-menu-btn">
                  Back to Menu
                </Button>
                {(() => {
                  const nextInfo = getNextLevelInfo();
                  if (!nextInfo) {
                    return (
                      <Button 
                        onClick={() => navigate("/dashboard")} 
                        className="btn-primary"
                        data-testid="quiz-finish-btn"
                      >
                        Finish <ChevronRight size={18} />
                      </Button>
                    );
                  }
                  if (nextInfo.locked) {
                    return (
                      <Button 
                        onClick={() => navigate("/course")} 
                        className="bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                        data-testid="quiz-unlock-next-btn"
                      >
                        <Lock size={16} className="mr-2" />
                        {nextInfo.reason}
                      </Button>
                    );
                  }
                  return (
                    <Button 
                      onClick={() => startGame(nextInfo.level)} 
                      className="btn-primary"
                      data-testid="quiz-next-level-btn"
                    >
                      Next Level <ChevronRight size={18} />
                    </Button>
                  );
                })()}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
